// Updated AttendanceSummary.js
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Avatar,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Header from "layouts/attendance/component/header";
import Table from "layouts/attendance/component/table";
import { reportees } from "layouts/attendance/dummy/helper";
import OvertimeDrawer from "../component/OvertimeDrawer";
import useOvertimeStore from "store/useOvertimeStore";
import OvertimeHistory from "../component/OvertimeHistory";
import MyOvertimeRequest from "../component/MyOvertime";
import OvertimeRequest from "../component/OvertimeRequest";
import GapReasons from "../component/GapReasons";
import AttendenceTeam from "../component/AttendenceTeam";
import TimelineDetailsDrawer from "../component/TimelineDetailsDrawer";
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";
import { EnhancedDayRow } from "../component/attendance";

// Import your existing API functions
import { getAttendenceStatus, attendenceMonthlySummary, attendenceTeam } from "api/attendance";

// Hooks
import { useAttendanceData } from "../hooks/useAttendanceData";
import { useCurrentTime } from "../hooks/useCurrentTime";
import FlagLoader from "layouts/flagloader";

// Utils
import {
  dateKey,
  fmtTime,
  formatTimer,
  getTodayRange,
  shiftDateBy,
  getWeekFromDate,
  getWeekDays,
  formatDateToAPI,
} from "../utils/timeUtils";
import AttendanceCalendar from "../component/AttendanceCalendar";
import TeamMemberAttendance from "../component/TeamMemberAttendance";
import TeamMemberAttendanceWrapper from "../component/TeamMemberAttendanceWrapper";

// Main Component
function AttendanceSummary({ userId = null, isTeamView = false, onBack }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { userPermission, user } = useAuth();

  // State
  const [activeOvertimeSubTab, setActiveOvertimeSubTab] = useState(null);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedDayForDetails, setSelectedDayForDetails] = useState(() => new Date());
  const [overtimeDrawerOpen, setOvertimeDrawerOpen] = useState(false);
  const [selectedOvertimeDate, setSelectedOvertimeDate] = useState(null);
  const [dateRange, setDateRange] = useState(getTodayRange());
  const [checkOutNote, setCheckOutNote] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [activeView, setActiveView] = useState("list");
  const [activeDataTab, setActiveDataTab] = useState("mydata");
  const [selectedTeamUser, setSelectedTeamUser] = useState(null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [checkIns, setCheckIns] = useState([]);
  const [checkOuts, setCheckOuts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [timer, setTimer] = useState(0);
  const [liveCheckedIn, setLiveCheckedIn] = useState(false);
  const [liveTimer, setLiveTimer] = useState("00:00:00");

  // New states for API data
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [teamAttendanceData, setTeamAttendanceData] = useState({});

  // Refs
  const timerRef = useRef();

  // Hooks
  const { currentTime, currentTimeMinutes } = useCurrentTime();
  const {
    statusData,
    processedTimelineData,
    loading: hookLoading,
    refreshing: hookRefreshing,
    handleRefresh,
  } = useAttendanceData();

  // Store
  const overtimeStore = useOvertimeStore();

  const todayKey = dateKey(new Date());

  // Function to fetch attendance data based on date range
  const fetchAttendanceData = useCallback(async (startDate, endDate, userId = null) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        from: formatDateToAPI(startDate),
        to: formatDateToAPI(endDate),
        ...(userId && { user_id: userId }),
      };

      // Use your existing API endpoint
      const response = await getAttendenceStatus(params);

      // Transform API response to match our format
      const transformedData = transformAttendanceData(response.data, startDate, endDate);
      setAttendanceData(transformedData);
    } catch (err) {
      setError(err.message || "Failed to fetch attendance data");
      console.error("Error fetching attendance data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to fetch team attendance data
  const fetchTeamAttendanceData = useCallback(async (startDate, endDate) => {
    try {
      const params = {
        from: formatDateToAPI(startDate),
        to: formatDateToAPI(endDate),
      };

      const response = await attendenceTeam(params);
      setTeamAttendanceData(response.data);
    } catch (err) {
      console.error("Error fetching team attendance:", err);
    }
  }, []);

  // Function to transform API data to our format
  const transformAttendanceData = (apiData, startDate, endDate) => {
    const transformed = {};

    // If apiData is an array
    if (Array.isArray(apiData)) {
      apiData.forEach((record) => {
        const dateKey = new Date(record.date).toISOString().split("T")[0];

        transformed[dateKey] = {
          date: new Date(record.date),
          status: record.attendance_status || record.status || "unknown",
          color: getAttendanceColor(record.attendance_status || record.status),
          comment: record.remarks || record.reason || "",
          checkIns: record.check_ins || [],
          checkOuts: record.check_outs || [],
          hoursWorked: formatHoursWorked(record.total_hours),
          isToday: dateKey === todayKey,
          isCheckedIn: record.is_checked_in || false,
          rawData: record,
        };
      });
    }
    // If apiData is an object with date keys
    else if (typeof apiData === "object") {
      Object.keys(apiData).forEach((dateKey) => {
        const record = apiData[dateKey];

        transformed[dateKey] = {
          date: new Date(dateKey),
          status: record.attendance_status || record.status || "unknown",
          color: getAttendanceColor(record.attendance_status || record.status),
          comment: record.remarks || record.reason || "",
          checkIns: record.check_ins || [],
          checkOuts: record.check_outs || [],
          hoursWorked: formatHoursWorked(record.total_hours),
          isToday: dateKey === todayKey,
          isCheckedIn: record.is_checked_in || false,
          rawData: record,
        };
      });
    }

    // Fill in missing dates with default data
    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const key = currentDate.toISOString().split("T")[0];

      if (!transformed[key]) {
        transformed[key] = getDefaultAttendanceData(currentDate);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return transformed;
  };

  // Function to get default attendance data for missing dates
  const getDefaultAttendanceData = (date) => {
    const key = date.toISOString().split("T")[0];
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6;

    let status = "present";
    let color = "#10B981";

    if (isWeekend) {
      status = "weekend";
      color = "#F97316";
    }

    return {
      date: date,
      status: status,
      color: color,
      comment: status,
      checkIns: [],
      checkOuts: [],
      hoursWorked: status === "present" ? "08:00" : "00:00",
      isToday: key === todayKey,
      isCheckedIn: false,
    };
  };

  // Function to format hours worked
  const formatHoursWorked = (totalHours) => {
    if (!totalHours) return "00:00";

    if (typeof totalHours === "number") {
      const hours = Math.floor(totalHours);
      const minutes = Math.floor((totalHours - hours) * 60);
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    }

    return totalHours;
  };

  // Function to get color based on attendance status
  const getAttendanceColor = (status) => {
    if (!status) return "#6B7280"; // Gray for unknown

    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "present":
      case "logged_in":
      case "checked_in":
        return "#10B981"; // Green

      case "absent":
      case "not_logged_in":
        return "#EF4444"; // Red

      case "halfday":
      case "half_day":
      case "halfday_leave":
        return "linear-gradient(90deg, #10B981 50%, #EF4444 50%)"; // Half green half red

      case "leave":
      case "on_leave":
        return "#8B5CF6"; // Purple

      case "weekend":
      case "weekend_off":
        return "#F97316"; // Orange

      case "holiday":
      case "public_holiday":
        return "#FFD700"; // Yellow

      case "late":
        return "#F59E0B"; // Amber

      default:
        return "#6B7280"; // Gray for unknown
    }
  };

  // Function to get status text for display
  const getStatusText = (status) => {
    if (!status) return "Unknown";

    const statusLower = status.toLowerCase();
    const statusMap = {
      present: "Present",
      logged_in: "Present",
      checked_in: "Present",
      absent: "Absent",
      not_logged_in: "Absent",
      halfday: "Half Day",
      half_day: "Half Day",
      halfday_leave: "Half Day Leave",
      leave: "Leave",
      on_leave: "On Leave",
      weekend: "Weekend",
      weekend_off: "Weekend Off",
      holiday: "Holiday",
      public_holiday: "Public Holiday",
      late: "Late",
    };

    return (
      statusMap[statusLower] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
    );
  };

  // Function to get attendance data for a specific date
  const getAttendanceForDate = (date) => {
    const key = new Date(date).toISOString().split("T")[0];
    const data = attendanceData[key];

    if (data) {
      return data;
    }

    // Return default data if no API data available
    return getDefaultAttendanceData(date);
  };

  // Fetch data when date range changes
  useEffect(() => {
    if (dateRange?.start && dateRange?.end) {
      if (activeDataTab === "mydata") {
        fetchAttendanceData(dateRange.start, dateRange.end);
      }
    }
  }, [dateRange, activeDataTab, fetchAttendanceData]);



  const handleOpenOvertimeDrawer = (date) => {
    setSelectedOvertimeDate(date);
    setOvertimeDrawerOpen(true);
  };

  const handleCheckIn = async () => {
    try {
      const now = new Date();
      // Note: You need to add checkIn API endpoint to your attendance.js
      // For now, we'll just update local state

      setCheckIns((prev) => [...prev, now]);
      setIsCheckedIn(true);
      setCheckInTime(now);
      setTimer(0);
      setLiveCheckedIn(true);

      // Refresh data after check-in
      if (dateRange?.start && dateRange?.end) {
        fetchAttendanceData(dateRange.start, dateRange.end);
      }

      // Show success message
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to check in");
    }
  };

  const handleCheckOut = async () => {
    try {
      const now = new Date();
      // Note: You need to add checkOut API endpoint to your attendance.js

      setCheckOuts((prev) => [...prev, now]);
      setIsCheckedIn(false);
      setCheckOutTime(now);
      clearInterval(timerRef.current);
      setLiveCheckedIn(false);

      // Refresh data after check-out
      if (dateRange?.start && dateRange?.end) {
        fetchAttendanceData(dateRange.start, dateRange.end);
      }

      setCheckOutNote(""); // Reset note
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to check out");
    }
  };

  const handlePrev = () => {
    setDateRange((prev) => ({
      start: shiftDateBy(prev.start, -7),
      end: shiftDateBy(prev.end, -7),
    }));
  };

  const handleNext = () => {
    setDateRange((prev) => ({
      start: shiftDateBy(prev.start, 7),
      end: shiftDateBy(prev.end, 7),
    }));
  };

  // Handle date selection from calendar
  const handleDateSelect = (date) => {
    const weekRange = getWeekFromDate(date);
    setDateRange(weekRange);
    setActiveView("list");
  };

  // Handle manual refresh
  const handleManualRefresh = () => {
    setRefreshing(true);
    if (dateRange?.start && dateRange?.end) {
      if (activeDataTab === "mydata") {
        fetchAttendanceData(dateRange.start, dateRange.end).finally(() => setRefreshing(false));
      }
    }
  };

  useEffect(() => {
    if (isCheckedIn) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isCheckedIn]);

  useEffect(() => {
    const mainTabs = [
      { value: "mydata", permission: "Attendance.MyData" },
      { value: "team", permission: "Attendance.Team" },
      { value: "overtime", permission: "Attendance.Overtime" },
      { value: "gapReasons", permission: "Attendance.GapReasons" },
      { value: "myovertime", permission: "Attendance.MyOvertime" },
    ].filter((tab) => hasPermission(tab.permission, userPermission));

    if (mainTabs.length > 0 && activeDataTab) {
      const currentTabHasPermission = mainTabs.some((tab) => tab.value === activeDataTab);

      if (!currentTabHasPermission) {
        setActiveDataTab(mainTabs[0].value);
      }
    }
  }, [userPermission, activeDataTab]);

  // Get days for current week
  const getDaysForWeek = () => {
    if (!dateRange?.start) return [];

    const startDate = new Date(dateRange.start);
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }

    return days.sort((a, b) => b - a); // Sort descending
  };

  const currentUser = {
    id: user?.id || userId,
    name: "You",
    shift: {
      name: "General",
      start_time: "09:00",
      end_time: "18:00",
    },
    shiftTime: "9:00 AM - 6:00 PM",
    image: "https://randomuser.me/api/portraits/lego/1.jpg",
  };

  const days = getDaysForWeek();
  const canViewGapReasons = hasPermission("Attendance.GapReasons", userPermission);

  const handleCustomDateRangeSelect = (customRange) => {
    setDateRange(customRange);
  };

  // Handle team user selection
  const handleTeamUserSelect = (user) => {
    setSelectedTeamUser(user);
    // Don't change activeDataTab - stay in "team" tab
  };

  // Handle back from team member view
  const handleBackFromTeamMember = () => {
    setSelectedTeamUser(null);
  };

  // AttendanceSummary.js mein handleClick function update karo
  const handleDayClick = useCallback((data) => {
    const { date, user } = data || {};

    // Ensure we have a user object
    const userData = user || selectedTeamUser || currentUser;

    setSelectedDayForDetails({
      date: date || new Date(),
      user: userData
    });
    setDetailsDrawerOpen(true);
  }, [selectedTeamUser, currentUser]);

  // Aur EnhancedDayRow mein user prop pass karo jaisa upar bataya

  // Update Header props
  const headerProps = {
    activeTab,
    onTabChange: setActiveTab,
    dateRange,
    activeView,
    setActiveView,
    setDateRange,
    shiftName: "General",
    shiftTime: "9:00 AM - 6:00 PM",
    checkOutNote,
    setCheckOutNote,
    checkOutTimer: formatTimer(timer),
    activeView,
    setActiveView,
    activeDataTab,
    setActiveDataTab,
    selectedUser: activeDataTab === "team" ? selectedTeamUser : currentUser,
    setSelectedUser: setSelectedTeamUser,
    userList: reportees,
    onPrev: handlePrev,
    onNext: handleNext,
    onCheckOut: handleCheckOut,
    onCheckIn: handleCheckIn,
    isCheckedIn,
    checkIns,
    checkOuts,
    activeOvertimeSubTab,
    setActiveOvertimeSubTab,
    onDateRangeSelect: handleCustomDateRangeSelect,
    selectedMonth,
    selectedYear,
    onMonthChange: setSelectedMonth,
    onYearChange: setSelectedYear,
    onRefresh: handleManualRefresh,
    isLoading: refreshing || loading,
    isTeamView: selectedTeamUser !== null // Show back button in header when viewing team member
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Header {...headerProps} />

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>

      {/* ================= OVERTIME ================= */}
      {activeDataTab === "overtime" && (
        <Box>
          {activeOvertimeSubTab === "request" &&
            hasPermission("Attendance.Overtime.Request", userPermission) && <OvertimeRequest />}
          {activeOvertimeSubTab === "history" &&
            hasPermission("Attendance.Overtime.History", userPermission) && <OvertimeHistory />}
          {activeOvertimeSubTab &&
            !hasPermission("Attendance.Overtime.Request", userPermission) &&
            !hasPermission("Attendance.Overtime.History", userPermission) && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="textSecondary">
                  You dont have permission to view overtime data.
                </Typography>
              </Box>
            )}
        </Box>
      )}

      {/* ================= GAP REASONS ================= */}
      {activeDataTab === "gapReasons" && canViewGapReasons && (
        <Box sx={{ mt: 2 }}>
          <GapReasons />
        </Box>
      )}

      {/* ================= MY OVERTIME ================= */}
      {activeDataTab === "myovertime" && hasPermission("Attendance.MyOvertime", userPermission) && (
        <Box sx={{ mt: 2 }}>
          <MyOvertimeRequest />
        </Box>
      )}

      {/* ================= TEAM ================= */}
      {activeDataTab === "team" && hasPermission("Attendance.Team", userPermission) && (
        <>
          {selectedTeamUser ? (
            // Use the wrapper component
            <TeamMemberAttendanceWrapper
              user={selectedTeamUser}
              onBack={handleBackFromTeamMember}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              reportees={reportees}
              timelineData={processedTimelineData}
              isTeamView={true}
              activeView={activeView} // ← Add this
              setActiveView={setActiveView} // ← Add this
            />
          ) : (
            // Show team list
            <Box mt={2}>
              <AttendenceTeam
                onUserSelect={handleTeamUserSelect}
              />
            </Box>
          )}
        </>
      )}

      {/* ================= MY DATA ================= */}
      {activeDataTab === "mydata" && hasPermission("Attendance.MyData", userPermission) && (
        <>
          {activeTab === "summary" && (
            <Box sx={{ p: isMobile ? 1 : 2, position: "relative" }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                  {/* <CircularProgress /> */}
                  <FlagLoader />
                  {/* <Typography sx={{ ml: 2 }}>Loading attendance data...</Typography> */}
                </Box>
              ) : (
                <>
                  {activeView === "list" && (
                    <>
                      <Box
                        sx={{
                          maxHeight: "calc(100vh - 200px)",
                          overflow: "auto",
                          pb: 1,
                          borderRadius: "12px",
                        }}
                      >
                        {/* Day Rows */}
                        {days.map((d, index) => {
                          const isToday = dateKey(d) === todayKey;
                          const dateString = d.getDate().toString().padStart(2, "0");
                          const weekdayName = d.toLocaleDateString("en-US", { weekday: "short" });
                          const dayLabel = isToday ? "Today" : `${weekdayName} ${dateString}`;

                          const attendanceDataForDay = getAttendanceForDate(d);

                          const entry = {
                            date: d,
                            status: getStatusText(attendanceDataForDay.status),
                            color: attendanceDataForDay.color,
                            comment: attendanceDataForDay.comment,
                            checkIns: attendanceDataForDay.checkIns,
                            checkOuts: attendanceDataForDay.checkOuts,
                            hoursWorked: attendanceDataForDay.hoursWorked,
                            isToday,
                            isCheckedIn: attendanceDataForDay.isCheckedIn,
                          };

                          return (
                            <EnhancedDayRow
                              key={index}
                              day={dayLabel}
                              date={dateString}
                              entry={entry}
                              timelineData={processedTimelineData}
                              currentTimeMinutes={currentTimeMinutes}
                              onDayClick={handleDayClick}
                              user={currentUser}
                              isToday={isToday}
                              currentDayData={statusData}
                              statusColor={entry.color}
                            />
                          );
                        })}
                      </Box>
                    </>
                  )}

                  {activeView === "tabular" && (
                    <Box mt={6}>
                      <Table
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                        onDayClick={handleDayClick}
                        timelineData={processedTimelineData}
                        weekDays={days}
                      />
                    </Box>
                  )}

                  {activeView === "calendar" && (
                    <Box>
                      <AttendanceCalendar
                        onDateRangeSelect={handleDateSelect}
                        selectedMonth={selectedMonth}
                        selectedYear={selectedYear}
                        onMonthChange={setSelectedMonth}
                        onYearChange={setSelectedYear}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </>
      )}

      {/* Show message if user doesn't have permission for selected tab */}
      {(activeDataTab === "mydata" && !hasPermission("Attendance.MyData", userPermission)) ||
        (activeDataTab === "team" && !hasPermission("Attendance.Team", userPermission)) ||
        (activeDataTab === "myovertime" && !hasPermission("Attendance.MyOvertime", userPermission)) ||
        (activeDataTab === "overtime" && !hasPermission("Attendance.Overtime", userPermission)) ||
        (activeDataTab === "gapReasons" && !canViewGapReasons) ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="textSecondary">
            You dont have permission to view this section.
          </Typography>
        </Box>
      ) : null}

      {/* Timeline Details Drawer */}
      <TimelineDetailsDrawer
        open={detailsDrawerOpen}
        onClose={() => {
          setDetailsDrawerOpen(false);
          setSelectedDayForDetails(null);
        }}
        selectedDate={selectedDayForDetails}
        setSelectedDate={setSelectedDayForDetails}
        user={selectedTeamUser || currentUser}
        timelineData={processedTimelineData}
      />

      <OvertimeDrawer
        open={overtimeDrawerOpen}
        onClose={() => {
          setOvertimeDrawerOpen(false);
          setSelectedOvertimeDate(null);
        }}
        selectedDate={selectedOvertimeDate}
      />
    </DashboardLayout>
  );
}

export default AttendanceSummary;
